/**
 * AdminCmsPages — list every CMS page across portals with filter tabs
 * and quick edit/create.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, FileText, Loader2, Search } from "lucide-react";
import { cmsApi } from "@/lib/cmsApi";

export default function AdminCmsPages() {
  const { user, loading: authLoading } = useAuth() || {};
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("");
  const [portal, setPortal] = useState("");
  const [q, setQ] = useState("");
  const [err, setErr] = useState(null);

  const refresh = async () => {
    setErr(null);
    try {
      const d = await cmsApi.list({ status, portal_slug: portal });
      setRows(d.pages || []); setCounts(d.counts || {});
    } catch (e) { setErr(e.message); }
  };

  useEffect(() => { if (user && !authLoading) refresh(); /* eslint-disable-next-line */ }, [status, portal, user, authLoading]);

  const canSee = user && (user.is_admin || user.platform_role === "editor" || user.platform_role === "super_admin");

  if (authLoading) return <div className="p-12 text-center text-slate-500">Loading…</div>;
  if (!canSee) return <div className="p-12 text-center text-slate-500" data-testid="cms-403">Editors + admins only.</div>;

  const filtered = (rows || []).filter((r) => !q || r.title.toLowerCase().includes(q.toLowerCase()) || r.slug.includes(q.toLowerCase()));

  return (
    <div className="max-w-[1100px] mx-auto px-4 lg:px-6 py-10" data-testid="admin-cms-pages">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-blue-700 mb-1.5">Editorial · CMS</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900"
              style={{ fontFamily: "'Lora', 'Georgia', serif" }}>
            Pages
          </h1>
        </div>
        <Link to="/admin/cms/pages/new"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-full transition-colors"
              data-testid="cms-new-btn">
          <Plus className="w-4 h-4" /> New page
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4 border-b border-slate-200 pb-3">
        {[["", "All"], ["draft", `Drafts (${counts.draft ?? 0})`], ["published", `Published (${counts.published ?? 0})`]].map(([k, l]) => (
          <button key={k || "all"} onClick={() => setStatus(k)}
                  className={`px-3 py-1.5 text-sm font-semibold rounded-full transition-colors ${
                    status === k ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                  }`}
                  data-testid={`cms-tab-${k || "all"}`}>
            {l}
          </button>
        ))}
        <input value={portal} onChange={(e) => setPortal(e.target.value)}
               placeholder="Filter by portal slug (blank = all)"
               className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 flex-1 min-w-[220px]"
               data-testid="cms-filter-portal" />
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search title or slug"
                 className="text-sm border border-slate-200 rounded-lg pl-8 pr-3 py-1.5"
                 data-testid="cms-filter-search" />
        </div>
      </div>

      {err && <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-900 mb-4">{err}</div>}

      {rows === null ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading pages…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-300 p-10 text-center text-slate-500"
             data-testid="cms-empty">
          No pages yet. <Link to="/admin/cms/pages/new" className="text-blue-600 hover:text-blue-800 underline">Create your first one →</Link>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="cms-list">
          {filtered.map((r) => (
            <li key={r.id}>
              <Link to={`/admin/cms/pages/${r.id}`}
                    className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all bg-white"
                    data-testid={`cms-item-${r.id}`}>
                <FileText className="w-4 h-4 text-slate-400 mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-slate-900">{r.title}</span>
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${
                      r.status === "published" ? "bg-green-50 text-green-700 border border-green-200" :
                      "bg-amber-50 text-amber-700 border border-amber-200"
                    }`}>{r.status}</span>
                  </div>
                  <div className="text-[12px] text-slate-500 mt-0.5">
                    /p/{r.slug} · portal: <strong>{r.portal_slug}</strong> · updated {(r.updated_at || "").slice(0, 16).replace("T", " ")}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
